/**
 * A deity's own path through the Web — allocation, the rules that govern it,
 * and the seams the simulation reads.
 *
 * The rules are `DESIGN.md` §6 and §8 as re-read by the 41-point ruling of
 * 2026-08-17 (`web.ts` header), and four of them are load-bearing:
 *
 *  · **Points are necessary and not sufficient.** Outer nodes carry a
 *    `Qualification` and are refused unless a `HistoryOracle` says the deity
 *    has actually lived it (D §6.2, `DESIGN-IV.md` §10) — and so, now, may an
 *    Epic. `oracle.ts` answers off the Chronicle and the anchors; the default
 *    oracle refuses everything, so an honest "you have not earned this" is
 *    what a Web with no world attached says.
 *  · **You walk outward by depth, not by exhaustion.** A layer opens once a
 *    god holds enough ordinary points in the direction — `TIER_FLOOR`,
 *    capped by what a thin direction actually holds beneath the layer — and
 *    a node stands on the points around it (`depthOutside`), so a refund can
 *    never leave an outer node hanging. Every layer holds more than the next
 *    one asks for; the surplus is the branch.
 *  · **The Epic is the forty-first point, and terminal.** It opens at
 *    `EPIC_REQUIRES` ordinary points in the direction, costs one more, must
 *    be lived for if it says so, and — alone among everything here — cannot
 *    be undone. `refund()` refuses it.
 *  · **At most two Epics, ever** (`MAX_EPICS`). A third is refused outright,
 *    and the arithmetic agrees: three journeys are 123 points and the whole
 *    theoretical budget is 120.
 *
 * `modifier()`, `bonus()`, `ceiling()`, `hasRule()` and `ruleDepth()` are the
 * seams the simulation and the interface read. Rules are the half of
 * progression that is not a percentage, and the Beasts direction is mostly
 * made of them: a rule changes what a selection can mean, what a scroll can
 * resolve into, what a verb can lean on.
 *
 * Pure by law 7: no three, no `Math.random`, no clock. What a deity has
 * chosen is a function of its choices, never of when it made them.
 */

import { EPICS } from './epics';
import type {
  Crossroad, Current, DirectionId, Effect, Epic, Qualification,
} from './types';
import {
  DIRECTIONS, DIRECTION_CAPACITY, EPIC_REQUIRES, LEGACY_POINTS_CEILING, MAX_EPICS,
  POINTS_AT_LEVEL_100, WEB_DATA, WebIndex, shippedIndex,
  type WebData,
} from './web';

export * from './types';
export {
  AXES, BEAST_CURRENTS, CROSSROADS, DIRECTIONS, DIRECTION_CAPACITY, EPIC_JOURNEY,
  EPIC_REQUIRES, LEGACY_POINTS_CEILING, MAX_EPICS, NODES, POINTS_AT_LEVEL_100,
  TIER_FLOOR, TIER_ORDER, WEB_DATA, WebIndex, axisOf, directionById, floorOf,
  nodeById, nodesOf, oppositeOf, ordinaryPointsIn, shippedIndex, writtenToEpic,
} from './web';
export type { WebData } from './web';
export { EPICS, epicById, epicsOf } from './epics';
export { BINDINGS } from './bindings';

/**
 * Whether the deity has actually lived a thing. The Web asks; history
 * answers. Returns false for anything it cannot vouch for — an oracle that
 * guesses would make D §6.2 decorative.
 */
export type HistoryOracle = (q: Qualification) => boolean;

/** The honest default: a Web with no world attached can vouch for nothing. */
export const NO_HISTORY: HistoryOracle = () => false;

/**
 * A dev oracle for looking at the Web before history exists. Never the
 * default, and `test/godhood.ts` asserts the default refuses — otherwise the
 * qualification rule would quietly become a comment.
 */
export const ALL_HISTORY: HistoryOracle = () => true;

/** D §6.2. Level 1 opens one philosophy; level 100 grants the doc's ~100. */
export const pointsForLevel = (level: number): number =>
  Math.max(0, Math.min(POINTS_AT_LEVEL_100, Math.floor(level)));

export interface WebSnapshot {
  level: number;
  /** Extra points beyond the level track, capped by D §6.2's working ceiling. */
  legacy: number;
  /** nodeId → ranks taken. Sorted on write so a snapshot is byte-stable. */
  spent: Record<string, number>;
  /** direction → epic id. Permanent once written. */
  epics: Record<string, string>;
  /** Crossroad ids taken. Sorted. */
  crossroads: string[];
}

/** Why a purchase was refused — the UI shows this rather than greying out. */
export type Refusal =
  | 'unknown' | 'maxed' | 'no-points'
  | 'tier-locked' | 'unqualified' | 'epic-locked' | 'epic-taken' | 'epic-limit'
  | 'crossroad-prereq' | 'already';

export interface Verdict {
  ok: boolean;
  why?: Refusal;
  /** Plain sentence for the UI. Empty when ok. */
  says: string;
}

const YES: Verdict = { ok: true, says: '' };
const no = (why: Refusal, says: string): Verdict => ({ ok: false, why, says });

const plural = (n: number, one: string, many = `${one}s`): string =>
  `${n} ${n === 1 ? one : many}`;

export class GodhoodWeb {
  /** D §6. What the deity has learned; the Web is what it became. */
  level = 1;
  legacy = 0;

  private readonly spent = new Map<string, number>();
  private readonly epics = new Map<DirectionId, string>();
  private readonly crossroads = new Set<string>();
  private readonly index: WebIndex;

  /**
   * `data` is the shipped Web unless a test hands in a synthetic one — the
   * engine's arithmetic (two Epics, never three; forty then the forty-first)
   * has to be provable against directions written to the full shape, and
   * only Beasts is today.
   */
  constructor(private oracle: HistoryOracle = NO_HISTORY, data?: WebData) {
    this.index = data ? new WebIndex(data) : shippedIndex();
  }

  /** Swap the oracle — the seam a real history reader plugs into. */
  setOracle(o: HistoryOracle): void { this.oracle = o; }

  /* ── accounting ────────────────────────────────────────────────────── */

  get pointsTotal(): number {
    return pointsForLevel(this.level) + Math.min(LEGACY_POINTS_CEILING, Math.max(0, this.legacy));
  }

  get pointsSpent(): number {
    let n = 0;
    for (const v of this.spent.values()) n += v;
    n += this.epics.size;                      // the Epic costs its own point
    for (const id of this.crossroads) n += this.index.crossroadById(id)?.cost ?? 0;
    return n;
  }

  get pointsFree(): number { return this.pointsTotal - this.pointsSpent; }

  /** Ranks taken in one node. */
  ranksIn(nodeId: string): number { return this.spent.get(nodeId) ?? 0; }

  /** Every point sunk into a direction, Epic included. */
  pointsIn(d: DirectionId): number {
    return this.ordinaryIn(d) + (this.epics.has(d) ? 1 : 0);
  }

  /** Ordinary points in a direction — what the Epic and the floors count. */
  ordinaryIn(d: DirectionId): number {
    let n = 0;
    for (const node of this.index.nodesOf(d)) n += this.ranksIn(node.id);
    return n;
  }

  /** Ordinary points in a direction held OUTSIDE one node — what that node stands on. */
  depthOutside(d: DirectionId, nodeId: string): number {
    return this.ordinaryIn(d) - this.ranksIn(nodeId);
  }

  /**
   * Where a god's emphasis in a direction has actually gone: ranks in nodes
   * tagged with a Current. A reading for the panel, never a gate — the
   * ruling is that Currents are discovered through investment, not chosen.
   */
  currentPointsIn(d: DirectionId, current: Current): number {
    let n = 0;
    for (const node of this.index.nodesOf(d)) if (node.current === current) n += this.ranksIn(node.id);
    return n;
  }

  epicOf(d: DirectionId): Epic | undefined {
    const id = this.epics.get(d);
    return id ? this.index.epicById(id) : undefined;
  }

  /** How many Epics this deity holds, of the `MAX_EPICS` it ever may. */
  get epicCount(): number { return this.epics.size; }

  hasCrossroad(id: string): boolean { return this.crossroads.has(id); }

  /**
   * How much of the Web as designed this deity occupies. The denominator is
   * fourteen directions at their design capacity, so the number means the
   * same thing before and after the remaining directions are written.
   */
  get breadth(): number {
    return this.pointsSpent / (DIRECTIONS.length * DIRECTION_CAPACITY);
  }

  /* ── the rules ─────────────────────────────────────────────────────── */

  canSpend(nodeId: string): Verdict {
    const node = this.index.nodeById(nodeId);
    if (!node) return no('unknown', 'no such node');
    if (this.ranksIn(nodeId) >= node.ranks) return no('maxed', 'already taken as far as it goes');
    if (this.pointsFree < 1) return no('no-points', 'no Godhood Points left to spend');

    const floor = this.index.floorOf(node);
    const have = this.depthOutside(node.direction, nodeId);
    if (have < floor) {
      return no('tier-locked',
        `walk further in first — ${plural(floor - have, 'more point')} in this direction`);
    }

    if (node.qualifies && !this.oracle(node.qualifies))
      return no('unqualified', node.qualifies.describes);

    return YES;
  }

  /**
   * Whether history has earned this node — its qualification answered, or
   * none asked. Separate from `canSpend`, which refuses on points and depth
   * BEFORE it asks the oracle: the panel needs to say *something has become
   * possible because of how you acted* even to a god who cannot yet afford
   * it, because that sentence is the whole of `PLAYABLE.md` P5.
   */
  lived(nodeId: string): boolean {
    const node = this.index.nodeById(nodeId);
    return !!node && (!node.qualifies || this.oracle(node.qualifies));
  }

  spend(nodeId: string): Verdict {
    const v = this.canSpend(nodeId);
    if (v.ok) this.spent.set(nodeId, this.ranksIn(nodeId) + 1);
    return v;
  }

  /**
   * Give a point back. Ordinary nodes only — D §8.2 says Epics are not
   * respec-able, and the refusal here is what makes that a rule rather than
   * an intention. A refund that would leave an outer node standing on less
   * depth than it asks for is refused too, so the allocation is legal at
   * every moment.
   */
  refund(nodeId: string): Verdict {
    const node = this.index.nodeById(nodeId);
    if (!node) return no('unknown', 'no such node');
    if (this.ranksIn(nodeId) < 1) return no('already', 'nothing invested here');

    this.spent.set(nodeId, this.ranksIn(nodeId) - 1);
    const stranded = this.strandedIn(node.direction);
    if (stranded) {
      this.spent.set(nodeId, this.ranksIn(nodeId) + 1);   // put it back
      return no('tier-locked', `${stranded} depends on this being spent`);
    }
    if (this.ranksIn(nodeId) === 0) this.spent.delete(nodeId);
    return YES;
  }

  /** The first node (or the Epic) the current allocation no longer holds up. */
  private strandedIn(d: DirectionId): string | undefined {
    for (const n of this.index.nodesOf(d)) {
      if (this.ranksIn(n.id) > 0 && this.depthOutside(d, n.id) < this.index.floorOf(n)) return n.name;
    }
    const ordinary = this.ordinaryIn(d);
    // The Epic stands on the direction's forty. A direction written to the
    // old thin shape can hold an Epic from an older save (18 of 18); it keeps
    // it exactly as long as it stays spent out.
    if (this.epics.has(d) && ordinary < EPIC_REQUIRES && ordinary < this.index.ordinaryPointsIn(d))
      return this.epicOf(d)!.name;
    return undefined;
  }

  canChooseEpic(d: DirectionId, epicId: string): Verdict {
    if (this.epics.has(d)) return no('epic-taken', 'this direction has already answered, and the answer is permanent');
    if (this.epics.size >= MAX_EPICS) {
      return no('epic-limit',
        `a god may hold ${MAX_EPICS === 2 ? 'two' : String(MAX_EPICS)} Epics and no more — you have answered twice already`);
    }
    const epic = this.index.epicById(epicId);
    if (!epic || epic.direction !== d) return no('unknown', 'no such Epic for this direction');
    if (this.pointsFree < 1) return no('no-points', 'no Godhood Points left to spend');

    /**
     * "The end of a MASTERED direction" (D §8.2), read as the forty-first
     * point. Two refusals rather than one because they are two facts:
     *
     *  · A direction not WRITTEN to forty cannot be mastered. Reach and Root
     *    hold 18 and the eleven placeholder directions hold 1; refusing is
     *    honest, and the alternative — selling a permanent capstone on a
     *    direction with no body — is exactly what `WIRING.md` A2 caught.
     *  · A written direction must hold its forty.
     */
    const capacity = this.index.ordinaryPointsIn(d);
    if (capacity < EPIC_REQUIRES) {
      return no('epic-locked', capacity <= 1
        ? 'this direction has not been written yet — there is nothing in it to master'
        : `this direction holds ${capacity} ordinary points and an Epic asks for ${EPIC_REQUIRES} — it has not been written that deep yet`);
    }
    const have = this.ordinaryIn(d);
    if (have < EPIC_REQUIRES)
      return no('epic-locked', `master the direction first — ${EPIC_REQUIRES - have} of ${EPIC_REQUIRES} points still unspent`);

    if (epic.qualifies && !this.oracle(epic.qualifies))
      return no('unqualified', epic.qualifies.describes);

    return YES;
  }

  /** Permanent. There is deliberately no `unchooseEpic`. */
  chooseEpic(d: DirectionId, epicId: string): Verdict {
    const v = this.canChooseEpic(d, epicId);
    if (v.ok) this.epics.set(d, epicId);
    return v;
  }

  canTakeCrossroad(id: string): Verdict {
    const c = this.index.crossroadById(id);
    if (!c) return no('unknown', 'no such crossroad');
    if (this.crossroads.has(id)) return no('already', 'already taken');
    if (this.pointsFree < c.cost) return no('no-points', `needs ${c.cost} points`);
    for (const d of c.between) {
      if (this.pointsIn(d) < c.requiresEach)
        return no('crossroad-prereq', `needs ${c.requiresEach} points in ${d}`);
    }
    if (c.qualifies && !this.oracle(c.qualifies)) return no('unqualified', c.qualifies.describes);
    return YES;
  }

  takeCrossroad(id: string): Verdict {
    const v = this.canTakeCrossroad(id);
    if (v.ok) this.crossroads.add(id);
    return v;
  }

  /* ── what the simulation reads ─────────────────────────────────────── */

  /** Every effect this deity's path currently carries, node ranks included. */
  activeEffects(): Effect[] {
    const out: Effect[] = [];
    for (const [nodeId, ranks] of this.spent) {
      const node = this.index.nodeById(nodeId);
      if (!node) continue;
      // a node's effects apply once per rank taken — D §8's ranks are depth,
      // not alternatives, and the doc's rank text reads as more of the same
      for (let i = 0; i < ranks; i++) out.push(...node.effects);
    }
    for (const id of this.epics.values()) {
      const e = this.index.epicById(id);
      if (e) out.push(...e.effects);
    }
    for (const id of this.crossroads) {
      const c = this.index.crossroadById(id);
      if (c) out.push(...c.effects);
    }
    return out;
  }

  /**
   * The seam. Ask for a simulation variable, get the deity's current standing
   * on it. Multipliers and costs compound, adds sum, caps take the largest,
   * thresholds sum, and a `rule` is reported by `hasRule` / `ruleDepth`.
   *
   * A variable nobody has invested in returns 1 for multiplier-shaped reads
   * and 0 for additive ones — so a caller that wires this up before the Web
   * is populated changes nothing, which is the property that makes it safe to
   * wire early.
   */
  modifier(variable: string): number {
    let mul = 1;
    for (const e of this.activeEffects()) {
      if (e.variable !== variable) continue;
      if (e.kind === 'multiplier' || e.kind === 'cost') mul *= e.value;
    }
    return mul;
  }

  bonus(variable: string): number {
    let sum = 0;
    for (const e of this.activeEffects()) {
      if (e.variable === variable && (e.kind === 'add' || e.kind === 'threshold')) sum += e.value;
    }
    return sum;
  }

  ceiling(variable: string): number | undefined {
    let best: number | undefined;
    for (const e of this.activeEffects()) {
      if (e.variable === variable && e.kind === 'cap') best = Math.max(best ?? -Infinity, e.value);
    }
    return best;
  }

  hasRule(variable: string): boolean { return this.ruleDepth(variable) > 0; }

  /**
   * How many ranks of rule this deity holds on a variable — the reconciliation
   * `test/wiring.ts` §7 named as open. One rank turns a rule on; a rule node
   * with more ranks (Known Range I–III, Sign on the Ground I–II) is read by
   * its consumer as *how deep*, and `hasRule` stays a boolean for the many
   * consumers that only need on/off.
   */
  ruleDepth(variable: string): number {
    let n = 0;
    for (const e of this.activeEffects()) if (e.variable === variable && e.kind === 'rule') n++;
    return n;
  }

  /* ── persistence ───────────────────────────────────────────────────── */

  toSnapshot(): WebSnapshot {
    const spent: Record<string, number> = {};
    for (const k of [...this.spent.keys()].sort()) spent[k] = this.spent.get(k)!;
    const epics: Record<string, string> = {};
    for (const k of [...this.epics.keys()].sort()) epics[k] = this.epics.get(k)!;
    return {
      level: this.level, legacy: this.legacy,
      spent, epics, crossroads: [...this.crossroads].sort(),
    };
  }

  /**
   * Restore without re-running the rules. A saved path was legal when it was
   * made, and re-validating it against today's node data would silently drop
   * a deity's history the first time a node is renamed — which is exactly the
   * kind of quiet loss `SNAPSHOT_VERSION` exists to make loud instead. That
   * is also how a save from the 20-point Web survives the 41-point one: its
   * allocation is kept as it stands, and only new purchases meet the new
   * floors. Unknown ids are kept out, and `test/godhood.ts` asserts the round
   * trip.
   */
  static fromSnapshot(
    s: WebSnapshot | undefined, oracle: HistoryOracle = NO_HISTORY, data?: WebData,
  ): GodhoodWeb {
    const w = new GodhoodWeb(oracle, data);
    if (!s) return w;
    w.level = s.level ?? 1;
    w.legacy = s.legacy ?? 0;
    for (const [id, ranks] of Object.entries(s.spent ?? {})) {
      if (w.index.nodeById(id) && ranks > 0) w.spent.set(id, ranks);
    }
    for (const [d, id] of Object.entries(s.epics ?? {})) {
      if (w.index.epicById(id)) w.epics.set(d as DirectionId, id);
    }
    for (const id of s.crossroads ?? []) {
      if (w.index.crossroadById(id)) w.crossroads.add(id);
    }
    return w;
  }
}

/** Every distinct simulation variable the whole Web asks for. */
export const demandedVariables = (): string[] => {
  const seen = new Set<string>();
  const eat = (es: readonly Effect[]) => { for (const e of es) seen.add(e.variable); };
  for (const n of WEB_DATA.nodes) eat(n.effects);
  for (const e of EPICS) eat(e.effects);
  for (const c of WEB_DATA.crossroads as readonly Crossroad[]) eat(c.effects);
  return [...seen].sort();
};
